<template>
    <app-layout>
        <Head title="Add Team Member"/>
        <template #header>
            <div class="md:flex justify-between items-center pb-1">
                <div class="mr-4 md:mr-5 flex justify-start items-center">
                    <PageBackHeader
                        :href="route('team.index')"
                        label="Team Management"
                        subtitle="Add Team Member"
                    />
                </div>
            </div>
        </template>

        <div class="max-w-7xl mx-auto flex mb-6">
            <div class="w-full p-2 sm:px-6 lg:px-8">
                <!-- Trial Upgrade Banner -->
                <div v-if="!$page.props.subscribed" class="mb-6 p-3 md:p-5 md:px-6 bg-gray-200 md:flex justify-between items-center rounded-lg">
                    <p class="bold mb-3 md:mr-4 md:mb-0">
                        Upgrade your account to add team members and collaborate on contacts together.<br/>
                        <span class="font-bold">Each additional user is $10/month. Cancel anytime.</span><br/>
                        <span class="italic text-sm underline"><a href="#" target="_blank">Learn more about teams in TextSpot.</a></span>
                    </p>
                    <div class="text-center">
                        <Link :href="route('subscriptions.index')" class="w-full text-center">
                            <jet-button>
                                See Plans
                            </jet-button>
                        </Link>
                    </div>
                </div>
                
                <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <!-- Main Form -->
                    <div class="lg:col-span-2 relative">
                        <!-- Trial Overlay Message -->
                        <div v-if="!$page.props.subscribed" class="absolute inset-0 flex items-center justify-center bg-white bg-opacity-50 rounded-lg z-10">
                            <div class="text-center p-6 bg-white bg-opacity-50 mb-32" v-if="false">
                                <h3 class="text-lg font-semibold text-gray-900 mb-3">Subscribe to a plan now to add a team.</h3>
                                <Link :href="route('subscriptions.index')">
                                    <jet-button>
                                        See Plans
                                    </jet-button>
                                </Link>
                            </div>
                        </div>
                        
                        <div class="bg-white shadow-xl sm:rounded-lg p-6 lg:p-8" :class="{'pointer-events-none': !$page.props.subscribed }">
                            
                            <div class="mb-6">
                                <h3 class="text-lg font-semibold text-gray-900 mb-2">Invite Team Members</h3>
                                <p class="text-gray-600">They'll receive an email with instructions to join your TextSpot team.</p>
                            </div>

                            <form @submit.prevent="handleInviteMembers" class="space-y-8">
                                <!-- Email Addresses -->
                                <div>
                                    <label for="emails" class="block text-sm font-medium text-gray-700 mb-2">
                                        Email Addresses
                                    </label>
                                    <textarea
                                        id="emails"
                                        v-model="form.emails"
                                        placeholder="Enter email addresses separated by commas or new lines"
                                        rows="5"
                                        class="w-full border-gray-300 focus:border-orange-500 focus:ring-orange-500 rounded-md shadow-sm"
                                        :class="{ 
                                            'border-red-500 focus:border-red-500 focus:ring-red-500': form.errors.emails,
                                            'bg-gray-50 cursor-not-allowed': !$page.props.subscribed
                                        }"
                                        :disabled="!$page.props.subscribed"
                                        required
                                    ></textarea>
                                    <div v-if="form.errors.emails" class="text-red-600 text-sm mt-1">
                                        {{ form.errors.emails }}
                                    </div>
                                    <div v-else-if="form.errors['emails.*']" class="text-red-600 text-sm mt-1">
                                        {{ form.errors['emails.*'] }}
                                    </div>
                                    <div v-else-if="form.errors.error" class="text-red-600 text-sm mt-1">
                                        {{ form.errors.error }}
                                    </div>
                                    <p v-else class="text-sm text-gray-500 mt-2">
                                        You can enter multiple email addresses separated by commas or new lines
                                    </p>
                                </div>

                                <!-- Role Selection -->
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 mb-4">Choose Role</label>
                                    
                                    <div class="space-y-4">
                                        <!-- Admin Role Card -->
                                        <div class="relative">
                                            <input 
                                                type="radio" 
                                                id="admin" 
                                                value="admin" 
                                                v-model="form.role" 
                                                class="sr-only"
                                                :disabled="!$page.props.subscribed"
                                            />
                                            <label 
                                                for="admin" 
                                                class="flex cursor-pointer rounded-lg border-2 p-4 transition-all duration-200"
                                                :class="form.role === 'admin' 
                                                    ? 'border-purple-500 bg-purple-50' 
                                                    : 'border-gray-200 hover:border-gray-300'"
                                            >
                                                <div class="flex items-start gap-4 w-full">
                                                    <div class="flex items-center justify-center w-5 h-5 mt-1">
                                                        <div 
                                                            class="w-4 h-4 border-2 rounded-full flex items-center justify-center"
                                                            :class="form.role === 'admin' 
                                                                ? 'border-purple-500 bg-purple-500' 
                                                                : 'border-gray-300'"
                                                        >
                                                            <div 
                                                                v-if="form.role === 'admin'"
                                                                class="w-2 h-2 bg-white rounded-full"
                                                            ></div>
                                                        </div>
                                                    </div>
                                                    
                                                    <div class="flex-1">
                                                        <div class="mb-3">
                                                            <div class="flex items-center gap-2 mb-2">
                                                                <font-awesome-icon :icon="['fas', 'shield-alt']" class="text-purple-500" />
                                                                <h4 class="text-lg font-semibold text-gray-900">Admin Role</h4>
                                                            </div>
                                                            <p class="text-sm text-gray-600">Full access to all features and settings</p>
                                                        </div>
                                                        
                                                        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                            <div class="flex items-start gap-2">
                                                                <font-awesome-icon :icon="['fas', 'check-circle']" class="text-green-500 mt-0.5" />
                                                                <div>
                                                                    <p class="font-medium text-sm">Team Management</p>
                                                                    <p class="text-xs text-gray-500">Add, remove, and manage members</p>
                                                                </div>
                                                            </div>
                                                            <div class="flex items-start gap-2">
                                                                <font-awesome-icon :icon="['fas', 'check-circle']" class="text-green-500 mt-0.5" />
                                                                <div>
                                                                    <p class="font-medium text-sm">Billing & Settings</p>
                                                                    <p class="text-xs text-gray-500">Manage subscription and account</p>
                                                                </div>
                                                            </div>
                                                            <div class="flex items-start gap-2">
                                                                <font-awesome-icon :icon="['fas', 'check-circle']" class="text-green-500 mt-0.5" />
                                                                <div>
                                                                    <p class="font-medium text-sm">Contact Management</p>
                                                                    <p class="text-xs text-gray-500">Assign contacts to team members</p>
                                                                </div>
                                                            </div>
                                                            <div class="flex items-start gap-2">
                                                                <font-awesome-icon :icon="['fas', 'check-circle']" class="text-green-500 mt-0.5" />
                                                                <div>
                                                                    <p class="font-medium text-sm">Analytics & Reports</p>
                                                                    <p class="text-xs text-gray-500">Access all reports and analytics</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </label>
                                        </div>

                                        <!-- Member Role Card -->
                                        <div class="relative">
                                            <input 
                                                type="radio" 
                                                id="member" 
                                                value="member" 
                                                v-model="form.role" 
                                                class="sr-only"
                                                :disabled="!$page.props.subscribed"
                                            />
                                            <label 
                                                for="member" 
                                                class="flex cursor-pointer rounded-lg border-2 p-4 transition-all duration-200"
                                                :class="form.role === 'member' 
                                                    ? 'border-blue-500 bg-blue-50' 
                                                    : 'border-gray-200 hover:border-gray-300'"
                                            >
                                                <div class="flex items-start gap-4 w-full">
                                                    <div class="flex items-center justify-center w-5 h-5 mt-1">
                                                        <div 
                                                            class="w-4 h-4 border-2 rounded-full flex items-center justify-center"
                                                            :class="form.role === 'member' 
                                                                ? 'border-blue-500 bg-blue-500' 
                                                                : 'border-gray-300'"
                                                        >
                                                            <div 
                                                                v-if="form.role === 'member'"
                                                                class="w-2 h-2 bg-white rounded-full"
                                                            ></div>
                                                        </div>
                                                    </div>
                                                    
                                                    <div class="flex-1">
                                                        <div class="mb-3">
                                                            <div class="flex items-center gap-2 mb-2">
                                                                <font-awesome-icon :icon="['fas', 'users']" class="text-blue-500" />
                                                                <h4 class="text-lg font-semibold text-gray-900">Member Role</h4>
                                                            </div>
                                                            <p class="text-sm text-gray-600">Limited access with messaging capabilities</p>
                                                        </div>
                                                        
                                                        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                            <div class="flex items-start gap-2">
                                                                <font-awesome-icon :icon="['fas', 'check-circle']" class="text-green-500 mt-0.5" />
                                                                <div>
                                                                    <p class="font-medium text-sm">Manage Conversations</p>
                                                                    <p class="text-xs text-gray-500">Send and receive messages</p>
                                                                </div>
                                                            </div>
                                                            <div class="flex items-start gap-2">
                                                                <font-awesome-icon :icon="['fas', 'check-circle']" class="text-green-500 mt-0.5" />
                                                                <div>
                                                                    <p class="font-medium text-sm">Manage Contacts</p>
                                                                    <p class="text-xs text-gray-500">Add, edit, and organize contacts</p>
                                                                </div>
                                                            </div>
                                                            <div class="flex items-start gap-2">
                                                                <font-awesome-icon :icon="['fas', 'eye']" class="text-gray-400 mt-0.5" />
                                                                <div>
                                                                    <p class="font-medium text-sm text-gray-500">Limited Access</p>
                                                                    <p class="text-xs text-gray-400">
                                                                        Cannot access billing or team settings, view other member contacts and conversations
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </label>
                                        </div>
                                    </div>

                                    <!-- Security Note -->
                                    <div class="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                        <div class="flex items-start gap-3">
                                            <div class="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                                                <font-awesome-icon :icon="['fas', 'shield-alt']" class="text-blue-600 text-xs" />
                                            </div>
                                            <div>
                                                <p class="font-medium text-sm text-blue-900">Security Note</p>
                                                <p class="text-xs text-blue-700 mt-1">
                                                    Only invite people you trust as team members. Admins have full access to your account and billing information. You can change individual roles after they join.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- Form Actions -->
                                <div class="flex gap-3 pt-4">
                                    <jet-button 
                                        type="submit" 
                                        :disabled="(!form.emails.trim() || form.processing) || !$page.props.subscribed"
                                        :loading="form.processing"
                                    >
                                        <font-awesome-icon :icon="['fas', 'envelope']" class="mr-2" />
                                        Preview Invoice
                                    </jet-button>
                                    <jet-button 
                                        type="button" 
                                        secondary 
                                        @click="router.visit(route('team.index'))"
                                        :disabled="form.processing"
                                    >
                                        Cancel
                                    </jet-button>
                                </div>
                            </form>
                        </div>
                    </div>

                    <!-- FAQ Sidebar -->
                    <div class="lg:col-span-1">
                        <div class="bg-white shadow-xl sm:rounded-lg p-6 sticky top-4">
                            <div class="mb-6">
                                <div class="flex items-center gap-2 mb-2">
                                    <font-awesome-icon :icon="['fas', 'question-circle']" class="text-orange-500" />
                                    <h3 class="text-lg font-semibold text-gray-900">Frequently Asked Questions</h3>
                                </div>
                            </div>
                            
                            <div class="space-y-6">
                                <div v-for="(faq, index) in faqs" :key="index" class="space-y-2">
                                    <h4 class="font-medium text-sm text-gray-900 leading-5">{{ faq.question }}</h4>
                                    <p class="text-sm text-gray-600 leading-5">{{ faq.answer }}</p>
                                    <div v-if="index < faqs.length - 1" class="border-b border-gray-100 pt-2"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Invoice Preview Modal -->
        <div v-if="showPreview" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div class="relative top-20 mx-auto p-6 border max-w-lg w-full shadow-lg rounded-lg bg-white">
                <div class="mt-3">
                    <div class="flex items-center justify-between mb-4">
                        <h3 class="text-lg font-medium text-gray-900">Invoice Preview</h3>
                        <button @click="closePreview" class="text-gray-400 hover:text-gray-600">
                            <font-awesome-icon :icon="['fas', 'times']" />
                        </button>
                    </div>

                    <div v-if="previewData" class="space-y-4">
                        <div>
                            <p class="text-sm text-gray-600 mb-2">
                                Adding <span class="font-semibold">{{ previewData.user_count }}</span> team member(s)
                            </p>
                            <div class="bg-gray-50 rounded-lg p-3">
                                <div v-for="email in previewData.valid_emails" :key="email" class="text-sm text-gray-700">
                                    • {{ email }}
                                </div>
                            </div>
                        </div>

                        <div v-if="previewData.invoice" class="border-t pt-4">
                            <h4 class="font-bold text-gray-900 mb-2">Billing summary for next invoice</h4>
                            <div class="space-y-2 text-sm">
                                <div v-for="line in previewData.invoice.lines.data" :key="line.id" class="flex justify-between">
                                    <span>{{ line.description }}</span>
                                    <span>${{ (line.amount / 100).toFixed(2) }}</span>
                                </div>
                                <div class="border-t pt-2 flex justify-between font-semibold">
                                    <span>Total</span>
                                    <span>${{ (previewData.invoice.total / 100).toFixed(2) }}</span>
                                </div>
                                <p class="text-xs text-gray-500 mt-1">
                                    This amount will be prorated and added to your next invoice.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div v-if="previewLoading" class="text-center py-4">
                        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto"></div>
                        <p class="text-sm text-gray-600 mt-2">Calculating preview...</p>
                    </div>

                    <div v-if="previewError" class="text-center py-4">
                        <p class="text-sm text-red-600">{{ previewError }}</p>
                    </div>

                    <div class="flex gap-3 mt-6 pt-4 border-t">
                        <jet-button 
                            @click="confirmInvitations"
                            :disabled="previewLoading || previewError || form.processing"
                            :loading="form.processing"
                            class="flex-1"
                        >
                            Confirm & Send Invitations
                        </jet-button>
                        <jet-button 
                            @click="closePreview"
                            secondary
                            :disabled="form.processing"
                        >
                            Cancel
                        </jet-button>
                    </div>
                </div>
            </div>
        </div>
    </app-layout>
</template>

<script setup>
import { Head } from '@inertiajs/vue3';
import { router, useForm } from '@inertiajs/vue3';
import { ref } from 'vue';
import AppLayout from '@/Layouts/AppLayout.vue';

const form = useForm({
    emails: '',
    role: 'member',
    message: '',
});

// Preview modal state
const showPreview = ref(false);
const previewData = ref(null);
const previewLoading = ref(false);
const previewError = ref(null);

const faqs = [
    {
        question: "Do team members share a phone number?",
        answer: "Yes, all team members share the same phone number. Incoming messages are sent to the Inbox of the member who is assigned to the contact responding. If no member is assigned to a contact, the admin will be assigned."
    },
    {
        question: "Can the admin see all conversations?",
        answer: "Yes, admins have the ability to see and respond to all conversations in the Inbox."
    },
    {
        question: "Can multiple team members message the same contact?",
        answer: "No, only admins have the ability to send and receive messages to any contact, regardless of what team member owns the contact."
    },
    {
        question: "Can contacts be reassigned?",
        answer: "Yes, contacts can be reassigned to different team members. Only admins have the ability to reassign contacts after they have initially been assigned."
    }
];

const handleInviteMembers = async () => {
    if (!form.emails.trim()) return;

    // Clear any existing errors
    form.clearErrors();

    try {
        // Make preview request first (without opening modal)
        const response = await axios.post(route('team.preview'), {
            emails: form.emails,
            role: form.role,
            message: form.message
        });

        // If preview succeeds, show modal with data
        previewData.value = response.data;
        previewLoading.value = false;
        previewError.value = null;
        showPreview.value = true;

    } catch (error) {
        // If validation fails, show errors on form (don't open modal)
        if (error.response && error.response.status === 422) {
            // Handle validation errors
            if (error.response.data.errors) {
                // If it's an array of error messages, show them as 'emails' field errors
                if (Array.isArray(error.response.data.errors)) {
                    form.setError('emails', error.response.data.errors.join(' '));
                } else {
                    // Handle standard Laravel validation format
                    form.setError(error.response.data.errors);
                }
            }
        } else if (error.response && error.response.data.error) {
            // Handle single error message
            form.setError('emails', error.response.data.error);
        } else {
            // Handle unexpected errors
            form.setError('emails', 'Failed to generate invoice preview. Please try again.');
        }
    }
};

const confirmInvitations = () => {
    form.post(route('team.store'), {
        preserveState: true,
        preserveScroll: true,
        onSuccess: () => {
            closePreview();
            // Form will be redirected by the controller on success
        },
        onError: (errors) => {
            // Errors are handled automatically by Inertia and displayed via validation
            console.error('Invitation errors:', errors);
        }
    });
};

const closePreview = () => {
    showPreview.value = false;
    previewData.value = null;
    previewError.value = null;
    previewLoading.value = false;
};
</script>
